import { createRouter, createWebHistory } from 'vue-router';
import HomePage from './views/HomePage.vue';
import TierlistView from './views/TierlistView.vue';
import EditAlbum from './views/EditAlbum.vue';
import EditSession from './views/EditSession.vue';
import ChangeLog from './views/ChangeLog.vue';
import SessionManagementPage from './views/SessionManagementPage.vue';
import AddAlbumPage from './views/AddAlbumPage.vue';
import ErrorPage from './views/ErrorPage.vue';
import StatsView from './views/StatsView.vue';
import { applyPrivacyMetaTags, removeSocialMediaTags } from './utils/privacyMeta';
import { useAuthStore } from './stores/auth';
import { useViewStatePreservation } from './composables/useViewStatePreservation';
import { v2Routes, v2Guard } from './v2/routes';
import { installV2AuthInterceptor } from './v2/authInterceptor';

const routes = [
  { path: '/', name: 'HomePage', component: HomePage },
  { path: '/tierlist', name: 'TierlistView', component: TierlistView },
  { path: '/stats', name: 'StatsView', component: StatsView },
  { path: '/timeline', name: 'TimelineView', component: () => import('./views/TimelineView.vue') },
  { path: '/edit-album/:id', name: 'EditAlbum', component: EditAlbum, props: true },
  { path: '/sessions/:id/edit', name: 'EditSession', component: EditSession, props: true },
  { path: '/changelog', name: 'ChangeLog', component: ChangeLog },
  { path: '/manage-sessions', name: 'SessionManagementPage', component: SessionManagementPage },
  { path: '/add-album', name: 'AddAlbumPage', component: AddAlbumPage },

  {
    path: '/error',
    name: 'ErrorPage',
    component: ErrorPage,
    props: route => ({
      errorCode: route.query.code || '500',
      errorTitle: route.query.title || 'Something went wrong',
      errorMessage: route.query.message || 'An unexpected error occurred. Please try again or contact support if the problem persists.'
    })
  },
  ...v2Routes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: ErrorPage,
    props: {
      errorCode: '404',
      errorTitle: 'Page Not Found',
      errorMessage: 'The page you\'re looking for doesn\'t exist or may have been moved.'
    }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});
installV2AuthInterceptor(router);

// v2 (book club beta) guard. Only applies to routes under the v2 parent route.
router.beforeEach((to) => (to.matched.some((r) => r.meta.v2) ? v2Guard(to) : true));

// Authentication guard for admin-protected routes
router.beforeEach(async (to, from, next) => {
  // View state preservation: Clear state on explicit navigation away from main views
  const { clearViewState } = useViewStatePreservation();

  // Paths where view state is relevant (home, tierlist, edit album)
  const viewStatePaths = ['/', '/tierlist', '/edit-album'];
  const fromIsViewStatePath = viewStatePaths.some(p => from.path === p || from.path.startsWith('/edit-album/'));
  const toIsViewStatePath = viewStatePaths.some(p => to.path === p || to.path.startsWith('/edit-album/'));

  // Clear state if navigating away from view state paths to unrelated sections
  if (fromIsViewStatePath && !toIsViewStatePath) {
    console.log('[Router] Clearing view state on navigation away');
    clearViewState();
  }

  // Check if the route requires admin authentication
  if (to.meta.requiresAdminAuth) {
    const authStore = useAuthStore();

    // Check if user is already authenticated
    if (authStore.isAuthValid) {
      // Extend session on successful access
      authStore.extendSession();
      next();
    } else {
      // Store the intended destination to redirect after successful auth
      const intendedPath = to.fullPath;

      // Redirect to the same route with a query parameter to trigger auth modal
      // We'll handle the modal display in the component based on needsAuth query param
      if (!to.query.needsAuth) {
        next({
          path: to.path,
          query: { ...to.query, needsAuth: 'true', redirect: intendedPath },
          replace: true
        });
      } else {
        // If we're already showing the auth modal, stay on the route
        next();
      }
    }
  } else {
    // Route doesn't require admin auth, proceed normally
    next();
  }
});

// Apply privacy meta tags on every route change
router.afterEach(() => {
  // Ensure privacy meta tags are applied
  if (typeof document !== 'undefined') {
    applyPrivacyMetaTags();
    removeSocialMediaTags();
  }
});

export default router;
